import React from 'react';
import { Calendar, Download, Mail, Newspaper, FileText } from 'lucide-react';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
  MarketingHeroLightButton,
  MarketingGhostOnDark,
  MarketingPrimaryButton,
} from '../../components/home/MarketingPageChrome';

const PRESS_HERO =
  'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1800&q=80';

const Press = () => {
  const pressReleases = [
    {
      date: 'January 15, 2026',
      title: 'Platform reaches 10,000 active users',
      category: 'Company news',
      description:
        'We’re excited to announce that our platform has reached 10,000 active users, marking a significant milestone in our mission to connect clients with exceptional vendors.',
    },
    {
      date: 'December 20, 2025',
      title: 'Enhanced booking experience launches',
      category: 'Product',
      description:
        'We’ve launched new features including real-time availability, instant messaging, and improved search filters to make event planning even easier.',
    },
    {
      date: 'November 10, 2025',
      title: 'Partnership with top event venues',
      category: 'Partnerships',
      description:
        'We’re thrilled to announce partnerships with leading event venues across the country, expanding our network of trusted partners.',
    },
    {
      date: 'October 5, 2025',
      title: 'Expansion into new markets',
      category: 'Company news',
      description:
        'We’re expanding our services to new cities and regions, bringing our platform to more clients and vendors nationwide.',
    },
  ];

  const mediaKit = [
    { icon: Download, title: 'Brand logos', description: 'Logos and brand assets for coverage.' },
    { icon: FileText, title: 'Product images', description: 'High-resolution product screenshots.' },
    { icon: Newspaper, title: 'Fact sheet', description: 'Company information and statistics.' },
    { icon: Download, title: 'Team photos', description: 'Executive and team photography.' },
  ];

  return (
    <MarketingPage>
      <MarketingMediaHero
        title="Press center"
        subtitle="Latest news and media resources for journalists and partners."
        image={PRESS_HERO}
        actions={
          <>
            <MarketingHeroLightButton href="#press-releases">View releases</MarketingHeroLightButton>
            <MarketingGhostOnDark to="/contact?subject=Press%20inquiry">
              Media inquiry
            </MarketingGhostOnDark>
          </>
        }
      />

      <MarketingSection tone="white">
        <MarketingSplitIntro title="News & resources">
          <p>
            For interviews, statements, or assets — start with the releases below, or contact the
            press team directly.
          </p>
        </MarketingSplitIntro>
      </MarketingSection>

      <MarketingSection tone="white" id="press-releases" className="!pt-0 sm:!pt-0">
        <div className="mx-auto max-w-3xl">
          <MarketingReveal className="mb-8">
            <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
              Latest releases
            </h2>
          </MarketingReveal>
          <div className="divide-y divide-m-border-light border-y border-m-border-light">
            {pressReleases.map((release) => (
              <article key={release.title} className="py-8">
                <div
                  className="mb-3 flex flex-wrap items-center gap-3 text-xs"
                  style={{ color: '#8a8a8a' }}
                >
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {release.date}
                  </span>
                  <span>{release.category}</span>
                </div>
                <h3 className="mb-2 text-lg font-bold tracking-tight" style={{ color: '#0a0a0a' }}>
                  {release.title}
                </h3>
                <p className="text-[15px] leading-relaxed" style={{ color: '#6b6b6b' }}>
                  {release.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </MarketingSection>

      <MarketingSection tone="canvas">
        <div className="mx-auto max-w-6xl">
          <MarketingReveal className="mb-8 max-w-xl">
            <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
              Media kit
            </h2>
          </MarketingReveal>
          <MarketingFeatureGrid items={mediaKit} />
        </div>
      </MarketingSection>

      <MarketingSection tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <Mail className="mx-auto mb-5 h-6 w-6 text-white/70" />
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            Media inquiries
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">
            For interviews, assets, or statements — reach the press team.
          </p>
          <MarketingPrimaryButton
            to="/contact?subject=Press%20inquiry"
            className="!bg-white !text-m-ink hover:!bg-white/90"
          >
            Contact press
          </MarketingPrimaryButton>
        </MarketingReveal>
      </MarketingSection>
    </MarketingPage>
  );
};

export default Press;
